import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '@images/white_logo.png';
import '@styles/sidebar.css';

/* ──────────────────────────────────────────────
   Module definitions — each module has an icon,
   label, and a list of sub-links (pages).
   ────────────────────────────────────────────── */
interface SubLink {
  to: string;
  label: string;
}

interface Module {
  id: string;
  label: string;
  icon: React.ReactNode;
  links: SubLink[];
}

const MODULES: Module[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="3" width="7" height="7" rx="1.5"/>
        <rect x="14" y="3" width="7" height="7" rx="1.5"/>
        <rect x="14" y="14" width="7" height="7" rx="1.5"/>
        <rect x="3" y="14" width="7" height="7" rx="1.5"/>
      </svg>
    ),
    links: [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/live', label: 'Live Dashboard' },
    ],
  },
  {
    id: 'foodcourts',
    label: 'Food Courts',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/>
        <path d="M7 2v20"/>
        <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/>
      </svg>
    ),
    links: [
      { to: '/foodcourts', label: 'All Food Courts' },
      { to: '/foodApproval', label: 'Pending Approvals' },
      { to: '/menu-audit', label: 'Menu Audit' },
      { to: '/quality-compliance', label: 'Compliance' },
      { to: '/onboarding', label: 'Onboarding' },
      { to: '/sla-monitor', label: 'SLA Monitor' },
      { to: '/suspension', label: 'Suspensions' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    links: [
      { to: '/payouts', label: 'Payouts' },
      { to: '/commissions', label: 'Commissions' },
      { to: '/coupon-analytics', label: 'Offers & Coupons' },
      { to: '/refunds', label: 'Disputes & Refunds' },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    links: [
      { to: '/revenue-reports', label: 'Revenue Reports' },
      { to: '/health-scores', label: 'Health Scores' },
      { to: '/customer-insights', label: 'Customer Insights' },
      { to: '/heatmap', label: 'Demand Heatmap' },
      { to: '/comparison', label: 'Compare' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    links: [
      { to: '/customers', label: 'Customer Profiles' },
      { to: '/review-moderation', label: 'Reviews' },
      { to: '/support-tickets', label: 'Support Tickets' },
      { to: '/notifications', label: 'Notifications' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <line x1="3" y1="9" x2="21" y2="9"/>
        <line x1="9" y1="21" x2="9" y2="9"/>
      </svg>
    ),
    links: [
      { to: '/app-content', label: 'App Content' },
      { to: '/delivery-zones', label: 'Delivery Zones' },
      { to: '/bulk-operations', label: 'Bulk Operations' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    ),
    links: [
      { to: '/platform-settings', label: 'Platform Settings' },
      { to: '/feature-flags', label: 'Feature Flags' },
      { to: '/app-versions', label: 'App Versions' },
      { to: '/api-health', label: 'API Health' },
      { to: '/roles', label: 'Roles & Access' },
      { to: '/users', label: 'Admin Users' },
      { to: '/staff', label: 'Field Staff' },
      { to: '/audit-logs', label: 'Audit Logs' },
      { to: '/activity', label: 'Activity Feed' },
    ],
  },
];

/* ──────────────────────────────────────────────
   Sidebar Component — Atlassian-style
   ────────────────────────────────────────────── */
const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const activePath = location.pathname;

  // Which module flyout is open? null = collapsed
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);

  // Determine which module the current route belongs to
  const currentModule = MODULES.find(m => m.links.some(l => activePath === l.to));

  const handleModuleClick = (moduleId: string) => {
    if (activeModule === moduleId) {
      setActiveModule(null); // toggle off
    } else {
      setActiveModule(moduleId);
    }
  };

  // Close flyout when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        flyoutRef.current &&
        !flyoutRef.current.contains(target) &&
        !target.closest('.rail-btn')
      ) {
        setActiveModule(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close flyout on navigation
  useEffect(() => {
    setActiveModule(null);
  }, [activePath]);

  const openModule = MODULES.find(m => m.id === activeModule);

  return (
    <>
      {/* ── Icon Rail (always visible, 64px) ── */}
      <div className="rail">
        {/* Logo */}
        <div className="rail-logo">
          <img src={logo} alt="S" className="rail-logo-img" />
        </div>

        {/* Module buttons */}
        <div className="rail-modules">
          {MODULES.map(mod => {
            const isActive = currentModule?.id === mod.id;
            const isOpen = activeModule === mod.id;

            return (
              <button
                key={mod.id}
                className={`rail-btn ${isActive ? 'rail-btn-active' : ''} ${isOpen ? 'rail-btn-open' : ''}`}
                onClick={() => handleModuleClick(mod.id)}
                title={mod.label}
              >
                <span className="rail-btn-icon">{mod.icon}</span>
                <span className="rail-btn-label">{mod.label}</span>
              </button>
            );
          })}
        </div>

        {/* Bottom help */}
        <div className="rail-bottom">
          <button className="rail-btn" title="Help" onClick={() => window.open('mailto:support@sermyble.com')}>
            <span className="rail-btn-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4"/>
                <path d="M12 8h.01"/>
              </svg>
            </span>
            <span className="rail-btn-label">Help</span>
          </button>
        </div>
      </div>

      {/* ── Flyout Panel (opens contextually) ── */}
      {openModule && (
        <>
          <div className="flyout-backdrop" onClick={() => setActiveModule(null)} />
          <div className="flyout" ref={flyoutRef}>
            <div className="flyout-header">
              <h3 className="flyout-title">{openModule.label}</h3>
              <button className="flyout-close" onClick={() => setActiveModule(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <ul className="flyout-links">
              {openModule.links.map(link => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={`flyout-link ${activePath === link.to ? 'flyout-link-active' : ''}`}
                  >
                    {link.label}
                    {link.to === '/live' && <span className="flyout-live-dot" />}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </>
  );
};

export default Sidebar;
